import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { projects } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

const FILTERS = ["All", "Client Work", "MERN Stack", "Next JS", "React JS", "JavaScript"];

interface Project {
  id: number;
  title: string;
  image: string;
  tech: string[];
  live: string;
  github: string;
  category: string;
  isClient?: boolean;
}

// Featured projects surface first, in this order — everything else keeps its original order after them.
function priorityFor(title: string) {
  const t = title.toLowerCase();
  if (t.includes("coursetea") || t.includes("course tea")) return 0;
  if (t.includes("bicci")) return 1;
  if (t.includes("time ex") || t.includes("timeex")) return 2;
  return 3;
}

function ProjectCard3D({ project, idx }: { project: Project; idx: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const shineRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    const inner = innerRef.current;
    const glow = glowRef.current;
    const shine = shineRef.current;
    if (!card || !inner || !glow || !shine) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateX = ((y - cy) / cy) * -14;
    const rotateY = ((x - cx) / cx) * 14;

    gsap.to(inner, { rotateX, rotateY, duration: 0.25, ease: "power2.out", transformPerspective: 900 });

    const glowX = (x / rect.width) * 100;
    const glowY = (y / rect.height) * 100;
    glow.style.background = `radial-gradient(circle at ${glowX}% ${glowY}%, rgba(34,197,94,0.1) 0%, transparent 60%)`;

    const shineAngle = Math.atan2(y - cy, x - cx) * (180 / Math.PI);
    shine.style.background = `linear-gradient(${shineAngle + 90}deg, transparent 30%, rgba(255,255,255,0.03) 50%, transparent 70%)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    const inner = innerRef.current;
    const glow = glowRef.current;
    const shine = shineRef.current;
    if (!inner || !glow || !shine) return;
    gsap.to(inner, { rotateX: 0, rotateY: 0, duration: 0.7, ease: "elastic.out(1, 0.5)" });
    glow.style.background = "transparent";
    shine.style.background = "transparent";
  }, []);

  const isClientProject = !!project.isClient;

  return (
    <div
      ref={cardRef}
      className="relative cursor-pointer h-full"
      style={{ perspective: "1000px" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={innerRef}
        className="relative rounded-2xl overflow-hidden h-full flex flex-col"
        style={{
          transformStyle: "preserve-3d",
          background: "rgba(255,255,255,0.03)",
          border: isClientProject ? "1px solid rgba(255,255,255,0.16)" : "1px solid rgba(255,255,255,0.09)",
        }}
      >
        <div ref={glowRef} className="absolute inset-0 pointer-events-none z-10 rounded-2xl" />
        <div ref={shineRef} className="absolute inset-0 pointer-events-none z-10 rounded-2xl" />

        {/* Top accent */}
        <div
          className="h-[1px] w-full flex-shrink-0"
          style={{
            background: isClientProject
              ? "linear-gradient(90deg, transparent, rgba(34,197,94,0.7), transparent)"
              : "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
          }}
        />

        {/* Client ribbon */}
        {isClientProject && (
          <div
            className="absolute top-4 left-0 z-20 flex items-center gap-1.5 pl-3 pr-4 py-1 rounded-r-full text-[10px] font-black font-mono tracking-wider uppercase"
            style={{ background: "linear-gradient(90deg, rgba(34,197,94,0.9), rgba(16,185,129,0.7))", color: "#000" }}
          >
            <i className="ri-star-fill text-[9px]" />
            CLIENT
          </div>
        )}

        {/* Screenshot */}
        <div
          className="relative w-full overflow-hidden flex-shrink-0"
          style={{ height: "185px", transform: "translateZ(18px)" }}
        >
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover object-top"
            style={{ filter: "brightness(0.85) contrast(1.05)" }}
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,13,13,0.0) 40%, rgba(13,13,13,0.9) 100%)" }} />
          <span
            className="absolute bottom-1 right-3 font-black select-none pointer-events-none"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "2.8rem", lineHeight: 1, color: "rgba(255,255,255,0.05)" }}
          >
            {String(idx + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Content */}
        <div className="p-4 pt-3 flex flex-col flex-1" style={{ transform: "translateZ(12px)" }}>
          <div className="flex items-start justify-between gap-2 mb-2.5">
            <h3 className="text-white font-bold text-sm leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {project.title}
            </h3>
            {isClientProject && (
              <span
                className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
                style={{ background: "rgba(34,197,94,0.12)", color: "#86efac", border: "1px solid rgba(34,197,94,0.3)" }}
              >
                <span className="w-1 h-1 rounded-full bg-green-400 inline-block animate-pulse" />
                Live
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1 mb-4">
            {project.tech.slice(0, 4).map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded text-[10px] font-mono"
                style={{ background: "rgba(255,255,255,0.05)", color: "rgba(226,232,240,0.85)", border: "1px solid rgba(255,255,255,0.09)" }}
              >
                {t}
              </span>
            ))}
            {project.tech.length > 4 && (
              <span
                className="px-2 py-0.5 rounded text-[10px] font-mono"
                style={{ background: "rgba(34,197,94,0.08)", color: "rgba(134,239,172,0.85)", border: "1px solid rgba(34,197,94,0.18)" }}
              >
                +{project.tech.length - 4}
              </span>
            )}
          </div>

          <div className="flex-1" />
          <div className="h-px mb-3" style={{ background: "rgba(255,255,255,0.07)" }} />

          <div className="flex items-center gap-2">
            {project.live && (
              <a
                href={project.live}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-3d flex-1 py-2 text-xs"
                style={
                  isClientProject
                    ? { background: "linear-gradient(180deg, #22c55e 0%, #16a34a 100%)", color: "#000", fontWeight: 700, borderRadius: 8, boxShadow: "0 4px 0 #14532d, inset 0 1px 0 rgba(255,255,255,0.15)" }
                    : { background: "linear-gradient(180deg, #1d1d1d 0%, #141414 100%)", color: "#4ade80", fontWeight: 700, borderRadius: 8, border: "1px solid rgba(34,197,94,0.25)", boxShadow: "0 4px 0 #000, inset 0 1px 0 rgba(255,255,255,0.05)" }
                }
                onClick={(e) => e.stopPropagation()}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-external-link-line text-xs" />
                  {isClientProject ? "Visit Site" : "Live Demo"}
                </span>
              </a>
            )}
            {project.github ? (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-3d btn-3d-outline flex items-center justify-center gap-1.5 px-4 py-2 text-xs"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="ri-github-line text-xs" />
                Code
              </a>
            ) : (
              <div
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs cursor-not-allowed whitespace-nowrap"
                style={{ background: "rgba(255,255,255,0.03)", color: "rgba(156,163,175,0.7)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <i className="ri-lock-line text-xs" />
                Private
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const INITIAL_COUNT = 6;

export default function DevProjects() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Featured projects (CourseTea, BICCI, Time Ex) always lead; the rest keep their original relative order.
  const orderedProjects = useMemo(
    () =>
      [...projects].sort((a, b) => {
        const pa = priorityFor(a.title);
        const pb = priorityFor(b.title);
        if (pa !== pb) return pa - pb;
        return projects.indexOf(a) - projects.indexOf(b);
      }),
    [],
  );

  const filtered = activeFilter === "All" ? orderedProjects : orderedProjects.filter((p) => p.category === activeFilter);
  const displayed = showAll ? filtered : filtered.slice(0, INITIAL_COUNT);
  const hasMore = filtered.length > INITIAL_COUNT;

  const handleFilter = (f: string) => {
    setActiveFilter(f);
    setShowAll(false);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current, { opacity: 0, y: 60, rotateX: -20, transformPerspective: 1000 }, {
        opacity: 1, y: 0, rotateX: 0, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: headerRef.current, start: "top 85%", once: true },
      });
      gsap.fromTo(filterRef.current, { opacity: 0, y: 30, scale: 0.95 }, {
        opacity: 1, y: 0, scale: 1, duration: 0.7, ease: "back.out(1.5)",
        scrollTrigger: { trigger: filterRef.current, start: "top 88%", once: true },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!gridRef.current) return;
    const cards = gridRef.current.querySelectorAll(".project-card-wrap");
    gsap.fromTo(cards, { opacity: 0, y: 50, rotateX: -18, rotateY: 5, transformPerspective: 900 }, {
      opacity: 1, y: 0, rotateX: 0, rotateY: 0, duration: 0.6, stagger: 0.07, ease: "power3.out",
    });
  }, [activeFilter, showAll]);

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
        perspective: "1200px",
      }}
    >
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(34,197,94,0.05) 0%, transparent 65%)", filter: "blur(60px)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className="mb-12" style={{ transformStyle: "preserve-3d" }}>
          <p className="text-xs font-mono text-gray-400 tracking-widest uppercase mb-3">projects</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Things I&apos;ve{" "}<span className="font-black text-green-600">Built</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-xs leading-relaxed">A collection of projects — from client work to personal builds.</p>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        <div
          className="rounded-3xl p-6 md:p-10"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* Filter tabs */}
          <div ref={filterRef} className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-8">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => handleFilter(f)}
                className="btn-3d px-3 py-1.5 text-[10px] sm:px-5 sm:py-2 sm:text-xs font-semibold tracking-wide font-mono"
                style={
                  activeFilter === f
                    ? { background: "linear-gradient(180deg, #22c55e 0%, #16a34a 100%)", color: "#000", borderRadius: 8, boxShadow: "0 4px 0 #14532d, inset 0 1px 0 rgba(255,255,255,0.15)" }
                    : { background: "linear-gradient(180deg, #1a1a1a 0%, #141414 100%)", color: "rgba(226,232,240,0.8)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.09)", boxShadow: "0 3px 0 #000, inset 0 1px 0 rgba(255,255,255,0.05)" }
                }
              >
                {f}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayed.map((project, idx) => (
              <div key={project.id} className="project-card-wrap">
                <ProjectCard3D project={project} idx={idx} />
              </div>
            ))}
          </div>

          {/* Show More */}
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={() => {
                  if (showAll) {
                    setShowAll(false);
                    setTimeout(() => {
                      const el = document.getElementById("projects");
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 50);
                  } else {
                    setShowAll(true);
                  }
                }}
                className="btn-3d btn-3d-ghost flex items-center gap-2 px-5 py-2 text-xs sm:px-8 sm:py-3 sm:text-sm font-mono"
              >
                {showAll ? (
                  <><i className="ri-arrow-up-line" />Show Less</>
                ) : (
                  <>
                    <i className="ri-layout-grid-line" />
                    Show More Projects
                    <span className="ml-1 px-2 py-0.5 rounded-full text-[10px]" style={{ background: "rgba(34,197,94,0.14)", color: "#86efac" }}>
                      +{filtered.length - INITIAL_COUNT}
                    </span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* GitHub CTA */}
          <div className="mt-14 pt-8 flex justify-center" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
            <a
              href="https://github.com/hr-safin"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-green-400 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-github-fill text-base" />
              <span>Explore all repos on GitHub</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200 text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}